import { PDFDocument, StandardFonts, rgb, PDFFont, PDFPage } from 'pdf-lib';
import templateUrl from '~/assets/medical-verification-form.pdf';

const PAGE_HEIGHT = 792;

// Identity checkbox x-positions keyed by mp_certify_identity value
// (1=Client, 2=Spouse, 3=Parent of minor, 4=Legal Guardian, 5=POA)
const IDENTITY_BOX_X: Record<number, number> = {
    1: 150.2,
    2: 199.6,
    3: 255.5,
    4: 375.0,
    5: 469.1,
};

const joinName = (person: any): string =>
    [person?.first_name, person?.last_name].filter(Boolean).join(' ').trim();

// Format an ISO date string (YYYY-MM-DD) as M/D/YYYY without timezone shifts.
const formatSignatureDate = (value: string | null | undefined): string => {
    if (!value) return '';
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value);
    if (!match) return '';
    const [, year, month, day] = match;
    return `${Number(month)}/${Number(day)}/${year}`;
};

export const generateMedicalVerificationPdf = async (
    patient: any,
    application: any,
): Promise<Blob> => {
    const bytes = await fetch(templateUrl).then((r) => r.arrayBuffer());
    const doc = await PDFDocument.load(bytes);
    const page = doc.getPages()[0];
    const font = await doc.embedFont(StandardFonts.Helvetica);
    const fontBold = await doc.embedFont(StandardFonts.HelveticaBold);
    const fontSignature = await doc.embedFont(StandardFonts.HelveticaOblique);

    const medPro = patient?.medical_professional;
    const medProPerson = medPro?.person;

    // Only the CLIENT CONSENT section is auto-filled. The MEDICAL VERIFICATION
    // section (Name of Client, Diagnosis, expense checkboxes, comments) is left
    // blank for the client's medical professional to complete by hand.

    // --- Text fields: [text, lineX0, lineX1, labelBottom] ---
    const drawField = (
        text: string | null | undefined,
        x0: number,
        x1: number,
        labelBottom: number,
        textFont: PDFFont = font,
    ) => {
        const value = (text ?? '').toString().trim();
        if (!value) return;
        drawFittedText(page, textFont, value, x0 + 2, PAGE_HEIGHT - labelBottom + 2, x1 - x0 - 4);
    };

    // Consent signature: the typed name doubles as the printed name and the
    // e-signature; the signature date is stamped after it so it reads as signed.
    const consentName = (application?.mp_signature ?? '').toString().trim();
    const consentDate = formatSignatureDate(application?.mp_signature_date);
    drawField(consentName, 131, 338, 279.2); // Print Your Full Name
    drawField(
        consentDate ? `${consentName}   ${consentDate}` : consentName,
        364, 574, 279.2, fontSignature,
    ); // Sign

    drawField(joinName(medProPerson), 209.2, 574.2, 302.7); // Client's Medical Professional
    drawField(medPro?.medical_center?.name, 107.8, 572.9, 349.9); // Medical Center
    drawField(
        [medProPerson?.address_1, medProPerson?.address_2].filter(Boolean).join(', '),
        102.4, 572.5, 373.4,
    ); // Work Address
    drawField(medProPerson?.city, 56.4, 301.6, 396.9); // City
    drawField(medProPerson?.state, 333.1, 493.3, 396.9); // State
    drawField(medProPerson?.zip_code, 515.9, 570.9, 396.9); // Zip
    drawField(medProPerson?.phone, 67.2, 277.3, 420.6); // Phone
    drawField(medProPerson?.email, 310.0, 570.2, 420.6); // Email

    // --- Checkboxes (CLIENT CONSENT section only) ---
    if (application?.mp_give_consent_for_mp_to_provide_info) {
        drawCheck(page, fontBold, 37.1, 209.6); // I give consent
    }
    const identity = Number(application?.mp_certify_identity);
    if (IDENTITY_BOX_X[identity]) {
        drawCheck(page, fontBold, 37.1, 244.9); // "I certify that I am the" line
        drawCheck(page, fontBold, IDENTITY_BOX_X[identity], 244.9);
    }

    const blob = new Blob([await doc.save()], { type: 'application/pdf' });
    return blob;
};

// Draw text, shrinking font size to fit maxWidth, truncating as a last resort.
const drawFittedText = (
    page: PDFPage,
    font: PDFFont,
    text: string,
    x: number,
    y: number,
    maxWidth: number,
) => {
    let size = 10;
    while (size > 6 && font.widthOfTextAtSize(text, size) > maxWidth) {
        size -= 0.5;
    }
    let value = text;
    while (value.length > 1 && font.widthOfTextAtSize(value, size) > maxWidth) {
        value = value.slice(0, -1);
    }
    page.drawText(value, { x, y, size, font, color: rgb(0, 0, 0) });
};

// Draw an "X" centered in a 9.5pt checkbox at the given top-left (pdfplumber coords).
const drawCheck = (page: PDFPage, font: PDFFont, boxX: number, boxTop: number) => {
    page.drawText('X', {
        x: boxX + 1.6,
        y: PAGE_HEIGHT - boxTop - 8,
        size: 9,
        font,
        color: rgb(0, 0, 0),
    });
};

export const downloadMedicalVerificationPdf = async (
    campaign: any,
    patient: any,
    application: any,
): Promise<void> => {
    const blob = await generateMedicalVerificationPdf(patient, application);
    const clientName = joinName(patient?.person).replace(/\s+/g, '_');
    const ntafId = campaign?.cf_patient_ntaf_id || campaign?.ntaf_id || campaign?.id || '';
    const filename = `${ntafId}_${clientName}_Medical_Verification.pdf`.replace(/^_+/, '');

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};
